import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    TextField,
    Grid,
    Typography,
    Menu,
    MenuItem,
    Card,
    CardContent,
    Divider
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import StoreIcon from '@material-ui/icons/Store';
import RoomIcon from '@material-ui/icons/Room';
import { Dialog } from 'components';
import { create, update, show } from './api';

const REPLACERS = {
    year: '{{ ANNEE EN COURS }}',
    date: '{{ DATE EN COURS }}',
    merchant: '{{ RAISON SOCIALE }}',
    activity: '{{ ACTIVITE COMMERCANT }}',
    address: '{{ ADRESSE MARCHE }}',
    placeNumber: '{{ NUMERO DE PLACE }}',
    meters: '{{ NOMBRE DE METRES }}',
    amount: '{{ REDEVANCE }}',
    fullName: '{{ NOM PRENOM }}',
    job: '{{ METIER }}',
    decreeNumber: '{{ NUMERO ARRETE }}',
    seniority: '{{ DATE ANCIENNETE }}',
    nonSubscriberUnitPrice: '{{ TARIF NON ABONNE }}',
    subscriberUnitPrice: '{{ TARIF ABONNE }}',
    periodicity: '{{ PERIODE FACTURE }}'
};
const MENUS = { merchant: 'merchant', market: 'market' };

const FormDialog = props => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [menu, setMenu] = useState(null);
    const [loading, setLoading] = useState(false);
    const [label, setLabel] = useState('');
    const [text, setText] = useState('');

    const submit = () => {
        setLoading(true);
        const req = props.id ? update(props.id, { label, text }) : create({ label, text });

        req.then(() => handleClose())
            .catch(err => alert(err))
            .finally(() => setLoading(false));
    };

    const handleClose = () => {
        props.close();
        setLabel('');
        setText('');
    };

    const countReplacer = useMemo(() => {
        return Object.keys(REPLACERS).reduce(
            (prev, key) =>
                prev + ((text?.match(new RegExp(REPLACERS[key], 'g')) || []).length ?? 0),
            0
        );
    }, [text]);

    useEffect(() => {
        if (props.id) {
            setLoading(true);
            show(props.id)
                .then(res => {
                    setLabel(res.label);
                    setText(res.text);
                })
                .catch(err => alert(err))
                .finally(() => setLoading(false));
        }
    }, [props.id]);

    const openMenu = (event, targetMenu) => {
        setAnchorEl(event.currentTarget);
        setMenu(targetMenu);
    };

    const closeMenu = () => {
        setAnchorEl(null);
        setMenu(null);
    };

    return (
        <Dialog
            maxWidth="md"
            disabled={loading}
            loading={loading}
            title={props.id ? 'Modifier un modèle AOT' : 'Enregistrer un modèle AOT'}
            onClose={handleClose}
            action={
                <Button disabled={loading} onClick={submit} variant="contained" color="primary">
                    {props.id ? 'Modifier' : 'Enregistrer'}
                </Button>
            }
            open={props.open}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        helperText="Saisir un libellé pour l'AOT"
                        label="AOT"
                        name="label"
                        onChange={e => setLabel(e.target.value)}
                        required
                        value={label}
                        disabled={loading}
                        variant="outlined"
                    />
                </Grid>

                <Grid item xs={9}>
                    <TextField
                        fullWidth
                        label="Texte du modèle"
                        name="text"
                        onChange={e => setText(e.target.value)}
                        required
                        value={text}
                        disabled={loading}
                        multiline
                        variant="outlined"
                        rows={20}
                    />
                </Grid>

                <Grid item xs={3}>
                    <Card elevation={2}>
                        <CardContent>
                            <Typography variant="h6" style={{ marginBottom: 12 }}>
                                Champs dynamiques
                            </Typography>

                            <Grid container direction="column" spacing={1}>
                                <Grid item>
                                    <Button
                                        size="small"
                                        fullWidth
                                        variant="outlined"
                                        onClick={() =>
                                            setText(text + REPLACERS.year) || closeMenu()
                                        }
                                        startIcon={<CalendarTodayIcon />}>
                                        Année en cours
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Button
                                        size="small"
                                        fullWidth
                                        variant="outlined"
                                        onClick={() =>
                                            setText(text + REPLACERS.date) || closeMenu()
                                        }
                                        startIcon={<CalendarTodayIcon />}>
                                        Date en cours
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Button
                                        aria-haspopup="true"
                                        onClick={e => openMenu(e, MENUS.merchant)}
                                        size="small"
                                        variant="outlined"
                                        fullWidth
                                        startIcon={<StoreIcon />}
                                        endIcon={<ExpandMoreIcon />}>
                                        Commerçant
                                    </Button>
                                    <Menu
                                        anchorEl={anchorEl}
                                        keepMounted
                                        open={menu === MENUS.merchant}
                                        onClose={closeMenu}>
                                        <MenuItem
                                            onClick={() =>
                                                setText(text + REPLACERS.fullName) || closeMenu()
                                            }>
                                            Nom prénom
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                setText(text + REPLACERS.merchant) || closeMenu()
                                            }>
                                            Raison sociale
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                setText(text + REPLACERS.activity) || closeMenu()
                                            }>
                                            Activité
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                setText(text + REPLACERS.job) || closeMenu()
                                            }>
                                            Métier
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                setText(text + REPLACERS.decreeNumber) ||
                                                closeMenu()
                                            }>
                                            Numéro d'arrêté
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                setText(text + REPLACERS.seniority) || closeMenu()
                                            }>
                                            Date d'ancienneté
                                        </MenuItem>
                                    </Menu>
                                </Grid>
                                <Grid item>
                                    <Button
                                        aria-haspopup="true"
                                        onClick={e => openMenu(e, MENUS.market)}
                                        size="small"
                                        variant="outlined"
                                        fullWidth
                                        startIcon={<RoomIcon />}
                                        endIcon={<ExpandMoreIcon />}>
                                        Marché
                                    </Button>
                                    <Menu
                                        anchorEl={anchorEl}
                                        keepMounted
                                        open={menu === MENUS.market}
                                        onClose={closeMenu}>
                                        <MenuItem
                                            onClick={() =>
                                                setText(text + REPLACERS.address) || closeMenu()
                                            }>
                                            Adresse
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                setText(text + REPLACERS.placeNumber) || closeMenu()
                                            }>
                                            Numéro de place
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                setText(text + REPLACERS.meters) || closeMenu()
                                            }>
                                            Nombre de mètres
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                setText(text + REPLACERS.amount) || closeMenu()
                                            }>
                                            Redevance (€)
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                setText(text + REPLACERS.subscriberUnitPrice) ||
                                                closeMenu()
                                            }>
                                            Prix unitaire abonné (€)
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                setText(text + REPLACERS.nonSubscriberUnitPrice) ||
                                                closeMenu()
                                            }>
                                            Prix unitaire non abonné (€)
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                setText(text + REPLACERS.periodicity) || closeMenu()
                                            }>
                                            Période (en mois)
                                        </MenuItem>
                                    </Menu>
                                </Grid>
                            </Grid>

                            <Divider style={{ margin: '16px 0' }} />

                            <Typography variant="body2">
                                Éléments remplacés :<strong> {countReplacer}</strong>
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Dialog>
    );
};

export default FormDialog;
